import React from "react";
import { createPortal } from "react-dom";
import { fetchExistingWorkCodes, fetchNextWorkCode } from "../../../api/clientV2";
import { useFloatingCoords } from "../../../hooks/useFloatingCoords";
import { distinctSwatchHex, swatchHex } from "../../../utils/color";
import type { ExistingWorkCode } from "../../../models";
import CodePill from "../CodePill/CodePill";
import "./WorkCodeField.css";

interface Props {
  scheduleId: number;
  code: string; // current code value ("" if none)
  name: string; // current label ("" if none)
  color: string; // current hex ("" if none)
  onChange: (patch: { code?: string; name?: string; color?: string }) => void;
  /** Codes created/assigned earlier in this editor session that aren't committed
   *  to the backend yet — merged into the list so they're reusable elsewhere. */
  extraCodes?: ExistingWorkCode[];
  /** Code VALUES to hide from suggestions (e.g. codes already on the palette
   *  when picking a code to add). Typing one verbatim still works. */
  excludeCodes?: Set<string>;
  /** Render the picker open and in-flow (no chip, no Cancel/Done) instead of
   *  behind a chip+popover — for surfaces that are already a dedicated panel. */
  inline?: boolean;
  /** Footer rendered at the bottom of the panel (e.g. a bulk "Assign to N"). */
  footer?: React.ReactNode;
  /** A small status pill rendered next to the chip: "matched" (reused an
   *  existing code) or "new" (auto-created from the group's prefix). */
  badge?: "matched" | "new" | null;
  /** Open straight into the create-a-new-code editor (used when the chip already
   *  holds an auto-created code the user is expected to tweak). */
  defaultNew?: boolean;
}

type Mode = "search" | "new";

/** One cohesive work-code control. Collapsed, it's a chip showing the picked
 *  code (or "Set work code"). Click it and the chip expands into a single search
 *  popover with two modes: SEARCH an existing board code (live results, the
 *  current pick pinned + highlighted at top) or create a NEW one (the search bar
 *  becomes the label, with a color + code editor below). Done keeps the change;
 *  Cancel reverts to whatever the field held when it opened. The popover is
 *  portaled to document.body and flips above the chip near the viewport bottom. */
const WorkCodeField: React.FC<Props> = ({
  scheduleId,
  code,
  name,
  color,
  onChange,
  extraCodes,
  excludeCodes,
  inline,
  footer,
  badge,
  defaultNew,
}) => {
  const [open, setOpen] = React.useState(false);
  const [mode, setMode] = React.useState<Mode>("search");
  const [search, setSearch] = React.useState("");
  const [debounced, setDebounced] = React.useState("");
  const [options, setOptions] = React.useState<ExistingWorkCode[]>([]);
  const wrap = React.useRef<HTMLDivElement>(null);
  const chipRef = React.useRef<HTMLButtonElement>(null);
  const popRef = React.useRef<HTMLDivElement>(null);
  const searchRef = React.useRef<HTMLInputElement>(null);
  const codeRef = React.useRef<HTMLInputElement>(null);
  const snapshot = React.useRef<{ code: string; name: string; color: string }>({
    code,
    name,
    color,
  });

  const active = inline || open;
  const floatingCoords = useFloatingCoords(chipRef, open, popRef, { flipAbove: false });
  const swatch = swatchHex(color, code);

  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 200);
    return () => clearTimeout(t);
  }, [search]);

  React.useEffect(() => {
    if (mode === "new" || debounced.length < 2) {
      setOptions([]);
      return;
    }
    let cancelled = false;
    fetchExistingWorkCodes(scheduleId, debounced).then((opts) => {
      if (!cancelled) setOptions(opts);
    });
    return () => {
      cancelled = true;
    };
  }, [scheduleId, debounced, mode]);

  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!wrap.current?.contains(target) && !popRef.current?.contains(target)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") cancel();
    };
    window.addEventListener("mousedown", onDoc);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDoc);
      window.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Focus the right input whenever the active mode changes.
  React.useEffect(() => {
    if (!active) return;
    if (mode === "new") codeRef.current?.focus();
    else searchRef.current?.focus();
  }, [active, mode]);

  const openPopover = () => {
    snapshot.current = { code, name, color };
    setSearch("");
    setMode(defaultNew && code ? "new" : "search");
    setOpen(true);
  };

  const done = () => setOpen(false);

  const cancel = () => {
    onChange({ ...snapshot.current }); // revert anything changed while open
    setOpen(false);
  };

  const pick = (opt: ExistingWorkCode) => {
    onChange({ code: opt.code, name: opt.prefix, color: opt.color ?? "" });
    setOpen(false); // a definitive choice — close (no-op in inline mode)
  };

  const enterNewMode = () => {
    const seed = search.trim();
    // Seed a color maximally distinct from this section's existing code colors
    // (sequential numeric codes hash to near-identical hues otherwise).
    const distinct = distinctSwatchHex((extraCodes ?? []).map((e) => e.color));
    onChange({ code: "", name: seed, color: distinct });
    setMode("new");
    fetchNextWorkCode(scheduleId).then((next) => {
      // The backend only knows committed codes — bump past any code created
      // earlier this session (staged adds it can't see), whether the caller
      // hands them over as extraCodes or excludeCodes.
      const taken = new Set((extraCodes ?? []).map((e) => e.code));
      excludeCodes?.forEach((c) => taken.add(c));
      let candidate = next;
      for (let guard = 0; taken.has(candidate) && guard < 500; guard++) {
        const num = parseInt(candidate, 10);
        if (!Number.isFinite(num)) break;
        candidate = String(num + 1);
      }
      onChange({ code: candidate });
    });
  };

  // Backspacing the label empty (or the ✕) discards the in-progress new code and
  // drops back to searching existing ones.
  const discardNew = () => {
    onChange({ code: "", name: "", color: "" });
    setSearch("");
    setMode("search");
  };

  const onLabelChange = (v: string) => {
    if (!v) {
      discardNew();
      return;
    }
    onChange({ name: v });
  };

  // Session-drafted codes (created earlier this session) first, then backend
  // matches; deduped, filtered by the search, and the current pick removed (it's
  // pinned separately at the top).
  const q = debounced.toLowerCase();
  const merged: ExistingWorkCode[] = [];
  const seen = new Set<string>();
  const isCurrent = (c: string, p: string) => c === code && p === name;
  for (const e of extraCodes ?? []) {
    const key = `${e.code}|${e.prefix}`;
    if (seen.has(key) || isCurrent(e.code, e.prefix)) continue;
    if (excludeCodes?.has(e.code)) continue;
    if (q && !`${e.code} ${e.prefix}`.toLowerCase().includes(q)) continue;
    seen.add(key);
    merged.push(e);
  }
  for (const o of options) {
    const key = `${o.code}|${o.prefix}`;
    if (seen.has(key) || isCurrent(o.code, o.prefix)) continue;
    if (excludeCodes?.has(o.code)) continue;
    seen.add(key);
    merged.push(o);
  }

  // The full record for the current pick (when it's an existing code we've
  // fetched) — lets the pinned row show the same count · sections meta as the
  // other results instead of a redundant "selected" (the ✓ already says that).
  const currentRecord = code
    ? (extraCodes ?? []).concat(options).find((o) => o.code === code && o.prefix === name)
    : undefined;

  const metaText = (o: ExistingWorkCode) => (
    <>
      {o.activity_count} activit{o.activity_count === 1 ? "y" : "ies"}
      {o.sections.length > 0 && (
        <>
          {" "}· {o.sections.slice(0, 3).join(", ")}
          {o.sections.length > 3 ? ` +${o.sections.length - 3}` : ""}
        </>
      )}
    </>
  );

  const searchBody = (
    <>
      <div className="work-code-field-search">
        <span className="work-code-field-search-icon" aria-hidden="true">⌕</span>
        <input
          ref={searchRef}
          className="work-code-field-search-input"
          value={search}
          placeholder="Search existing codes…"
          aria-label="Search existing work codes"
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="work-code-field-results" role="listbox">
        {code && (
          <div className="work-code-field-result is-picked is-current" aria-current="true">
            <span className="work-code-field-check" aria-hidden="true">✓</span>
            <CodePill code={code} color={swatch} className="work-code-field-badge" />
            <span className="work-code-field-prefix">{name || "(no label)"}</span>
            {currentRecord && (
              <span className="work-code-field-meta">{metaText(currentRecord)}</span>
            )}
          </div>
        )}
        {merged.map((opt) => (
          <button
            type="button"
            key={`${opt.code}|${opt.prefix}`}
            className="work-code-field-result"
            onClick={() => pick(opt)}
          >
            <CodePill code={opt.code} color={opt.color} className="work-code-field-badge" />
            <span className="work-code-field-prefix">{opt.prefix}</span>
            <span className="work-code-field-meta">{metaText(opt)}</span>
          </button>
        ))}
        {debounced.length >= 2 && merged.length === 0 && !code && (
          <div className="work-code-field-empty">No matching codes.</div>
        )}
      </div>

      <button type="button" className="work-code-field-add-new" onClick={enterNewMode}>
        <span aria-hidden="true">＋</span> New work code
      </button>
    </>
  );

  const newBody = (
    <>
      <div className="work-code-field-search">
        <span className="work-code-field-search-icon" aria-hidden="true">⌕</span>
        <input
          ref={searchRef}
          className="work-code-field-search-input"
          value={name}
          placeholder="New code label…"
          aria-label="New work code label"
          onChange={(e) => onLabelChange(e.target.value)}
        />
        <button
          type="button"
          className="work-code-field-discard"
          title="Discard new code"
          aria-label="Discard new code"
          onClick={discardNew}
        >
          ✕
        </button>
      </div>

      <div className="work-code-field-create">
        <div className="work-code-field-overline">New work code</div>
        <div className="work-code-field-create-row">
          <input
            type="color"
            className="work-code-field-color"
            value={swatch}
            onChange={(e) => onChange({ color: e.target.value })}
            title="Work code color"
            aria-label="Work code color"
          />
          <input
            ref={codeRef}
            className="work-code-field-code"
            value={code}
            placeholder="code"
            aria-label="New work code"
            onChange={(e) => onChange({ code: e.target.value })}
            onKeyDown={(e) => e.key === "Enter" && done()}
          />
        </div>
        <div className="work-code-field-hint">Auto-created — pick a color &amp; code.</div>
      </div>
    </>
  );

  const panelBody = (
    <>
      {mode === "new" ? newBody : searchBody}
      {footer && <div className="work-code-field-footer">{footer}</div>}
      {!inline && (
        <div className="work-code-field-actions">
          <button type="button" className="work-code-field-cancel" onClick={cancel}>
            Cancel
          </button>
          <button type="button" className="work-code-field-done" onClick={done}>
            Done
          </button>
        </div>
      )}
    </>
  );

  if (inline) {
    return (
      <div className="work-code-field work-code-field--inline" ref={wrap}>
        <div className="work-code-field-pop work-code-field-pop--inline" ref={popRef}>
          {panelBody}
        </div>
      </div>
    );
  }

  const popover = open ? (
    <div
      className="work-code-field-pop"
      ref={popRef}
      style={
        floatingCoords
          ? {
              top: floatingCoords.top,
              left: floatingCoords.left,
              maxHeight: Math.max(180, window.innerHeight - floatingCoords.top - 10),
            }
          : undefined
      }
    >
      {panelBody}
    </div>
  ) : null;

  return (
    <div className="work-code-field" ref={wrap}>
      <button
        type="button"
        ref={chipRef}
        className={`work-code-field-chip ${badge === "new" ? "is-new" : ""}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => (open ? setOpen(false) : openPopover())}
      >
        {code ? (
          <>
            <CodePill code={code} color={swatch} className="work-code-field-badge" />
            {name && <span className="work-code-field-chip-label">{name}</span>}
          </>
        ) : (
          <span className="work-code-field-chip-empty">
            Set work code
            <span className="work-code-field-chip-chevron" aria-hidden="true">▾</span>
          </span>
        )}
        {badge && (
          <span className={`work-code-field-tag work-code-field-tag--${badge}`}>
            {badge === "new" ? "＋ NEW" : "✓ matched"}
          </span>
        )}
      </button>
      {popover && createPortal(popover, document.body)}
    </div>
  );
};

export default WorkCodeField;
